// import { ConnectedRouter } from 'connected-react-router';
import React, { useEffect, useState, Suspense, lazy, useMemo, useCallback } from 'react';
import { Route, Routes, useNavigate } from 'react-router';
import { ToastContainer, Zoom } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LinkedInCallback } from 'react-linkedin-login-oauth2';
import { routes } from './routes-names';
import { getUser } from '../../redux/user';
import { getConsentWorkflowSettings } from '../../redux/consentWorkFlow';
import { getHRMSUser } from '../../redux/hrms';
import { localStorageGetItem } from '../../components/storage/localStorageManager';
import { useAppDispatch } from '../../redux/reduxhooks';
import ScrollToTop from './scroll-to-top';
import MarketPlaceCandidateRegistrationForm from '../marketplace-integrations/MarketPlaceCandidateSignUp';
import MarketPlaceBaseFormLayout from '../marketplace-integrations/MarketPlaceBaseFormLayout';
import Keka from '../marketplace-integrations/Keka';
import { isTalentForumEnabled } from '../../config/constants';

const OctoberEvent = lazy(() => import('../main/events/octoberEvent'));
const SignupLayout = lazy(() => import('../../components/registration/SignupLayout'));
const ProtectedRoute = lazy(() => import('./protected'));
const Room = lazy(() => import('../room'));
const About = lazy(() => import('../main/about'));
const Contact = lazy(() => import('../main/contact'));
const Legal = lazy(() => import('../main/legal/index'));
const Social = lazy(() => import('../registration/social'));
const Verification = lazy(() => import('../registration/verification'));
const EmployerVideo = lazy(() => import('../main/employervideo'));
const RegistrationProfile = lazy(() => import('../main/registration-profile'));
const Webinar1 = lazy(() => import('../main/webinar1'));
const Webinar2 = lazy(() => import('../main/webinar2'));
const Webinar3 = lazy(() => import('../main/webinar3'));
const Webinar4 = lazy(() => import('../main/webinar4'));
const Webinar5 = lazy(() => import('../main/webinar5'));
const Webinar6 = lazy(() => import('../main/webinar6'));
const Webinar7 = lazy(() => import('../main/webinar7'));
const Webinar8 = lazy(() => import('../main/webinar8'));
const Webinar9 = lazy(() => import('../main/webinar9'));
const Webinar10 = lazy(() => import('../main/webinar10'));
const Webinar11 = lazy(() => import('../main/webinar11'));
const Webinars = lazy(() => import('../main/webinars'));
const Podcast = lazy(() => import('../main/podcast'));
const VerifyCompanyLead = lazy(() => import('../main/VerifyCompanyLead'));
const VerifyScreenXSignIn = lazy(() => import('../registration/screenX-OfferX-SignIn'));
const Hrms = lazy(() => import('../main/hrms'));
const SuccessStories = lazy(() => import('../main/successStories'));
const Story1 = lazy(() => import('../main/stories/story1'));
// import Story2 from '../main/stories/story2';
const Story3 = lazy(() => import('../main/stories/story3'));
const ClaimProcess = lazy(() => import('../main/claimProcess'));
const SubscriptionPlans = lazy(() => import('../main/get-pricing/SubscriptionPlans'));
const EarnFreeSubscription = lazy(() => import('../main/get-pricing/EarnFreeSubscription'));
const BlogLanding = lazy(() => import('../main/blogLanding'));
const Blog1 = lazy(() => import('../main/blogs/blog1'));
const Blog2 = lazy(() => import('../main/blogs/blog2'));
const Blog3 = lazy(() => import('../main/blogs/blog3'));
const Blog4 = lazy(() => import('../main/blogs/blog4'));
const CandidatePublicProfile = lazy(() => import('../seo-search/candidatePublicProfile'));
const CompanyPublicProfile = lazy(() => import('../seo-search/companyPublicProfile'));
const CandPrivatePage = lazy(() => import('../seo-search/priv-component/candPrivatePage'));
const CompPrivatePage = lazy(() => import('../seo-search/priv-component/compPrivatePage'));
const AprilEvent = lazy(() => import('../main/events/aprilEvent'));
const AugustEvent = lazy(() => import('../main/events/augustEvent'));
const CandidateClaimRoute = lazy(() => import('../seo-search/candidateClaimRoute'));
const Privacy = lazy(() => import('../main/legal/privacy'));
const TermsAndConditions = lazy(() => import('../main/legal/terms-and-conditions'));
const FAQ = lazy(() => import('../main/faq'));
const MarketPlace = lazy(() => import('../marketplace-integrations/marketplace/MarketPlace'));

const LoadingSpinner = () => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '16px',
        }}
    >
        Loading...
    </div>
);

const AppRoutes = () => {
    const [apiToRun, setApiToRun] = useState(false);
    const [hrmsApiToRun, setHrmsApiToRun] = useState(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

   
    const goToRegistration = useCallback(() => {
        navigate(routes.registrationProfile);
    }, [navigate]);

    const runUserApis = useCallback(async () => {
        await dispatch(getUser(navigate));
        await dispatch(getConsentWorkflowSettings());
    }, [dispatch, navigate]);

    const runHrmsApis = useCallback(async () => {
        await dispatch(getHRMSUser());
        await dispatch(getConsentWorkflowSettings());
    }, [dispatch]);

    const runConsentApi = useCallback(async () => {
        await dispatch(getConsentWorkflowSettings());
    }, [dispatch]);

    useEffect(() => {
        if (apiToRun) {
            runUserApis();
        } else if (hrmsApiToRun) {
            runHrmsApis();
        } else {
            runConsentApi();
        }
    }, [apiToRun, hrmsApiToRun, runUserApis, runHrmsApis, runConsentApi]);

    const { shouldRunUserApi, shouldRunHrmsApi } = useMemo(() => {
        const userApiToRun = localStorageGetItem('userApiToBeRun');
        const hrmsUserApiToRun = localStorageGetItem('hrmsuserApiToBeRun');

        return {
            shouldRunUserApi: !!userApiToRun,
            shouldRunHrmsApi: !!hrmsUserApiToRun,
        };
    }, []);

    useEffect(() => {
        setApiToRun(shouldRunUserApi);
        setHrmsApiToRun(shouldRunHrmsApi);
    }, [shouldRunUserApi, shouldRunHrmsApi]);

    useEffect(() => {
        const currentPath = window.location.pathname + window.location.search + window.location.hash;
        // history.replace(currentPath);
        // navigate(currentPath, { replace: true });
        // navigate(currentPath);
    }, []);

    const toastContainerProps = useMemo(
        () => ({
            autoClose: 5000,
            transition: Zoom,
            theme: 'colored',
            hideProgressBar: true,
        }),
        []
    );

     const talentForumEnabled = String(isTalentForumEnabled).toLowerCase() === 'true' ? true : false;

    return (
        <>
            {/* <HistoryRouter history={history}> */}
            <ScrollToTop />
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    <Route path={routes.main} element={<ProtectedRoute />} />
                    <Route path="/*" element={<Room />} />
                    {/* <Route path={routes.main} element={<Main />} /> */}
                    <Route path={routes.about} element={<About />} />
                    <Route path={routes.marketplaceIntegrations} element={<MarketPlace />} />
                    <Route path={routes.marketplaceCandidateSignUp} element={<MarketPlaceBaseFormLayout />} />
                    <Route path={routes.contact} element={<Contact />} />
                    <Route path={routes.hrms} element={<Hrms />} />
                    <Route path={routes.verifyScreenXSignIn} element={<VerifyScreenXSignIn />} />
                    <Route path={routes.signUp} element={<SignupLayout />} />
                    <Route path={routes.legal} element={<Legal />}>
                        <Route path={routes.privacy} element={<Privacy />} />
                        <Route path={routes.terms} element={<TermsAndConditions />} />
                        <Route path={routes.faq} element={<FAQ />} />
                    </Route>
                    <Route path={routes.signinSignup} element={<Social goToRegistration={goToRegistration} />} />
                    <Route path={routes.verification} element={<Verification />} />
                    <Route path={routes.employervideo} element={<EmployerVideo />} />
                    <Route path={routes.registrationProfile} element={<RegistrationProfile />} />
                    {/* <Route path={routes.pricing} component={Pricing} /> */}
                    <Route path={routes.webinar1} element={<Webinar1 />} />
                    <Route path={routes.webinar2} element={<Webinar2 />} />
                    <Route path={routes.webinar3} element={<Webinar3 />} />
                    <Route path={routes.webinar4} element={<Webinar4 />} />
                    <Route path={routes.webinar5} element={<Webinar5 />} />
                    <Route path={routes.webinar6} element={<Webinar6 />} />
                    <Route path={routes.webinar7} element={<Webinar7 />} />
                    <Route path={routes.webinar8} element={<Webinar8 />} />
                    <Route path={routes.webinar9} element={<Webinar9 />} />
                    <Route path={routes.webinar10} element={<Webinar10 />} />
                    <Route path={routes.webinar11} element={<Webinar11 />} />
                    <Route path={routes.webinars} element={<Webinars />} />
                    <Route path={routes.podCast} element={<Podcast />} />
                    <Route path={routes.successStories} element={<SuccessStories />} />
                    <Route path={routes.story1} element={<Story1 />} />
                    {/* <Route path={routes.story2} element={<Story2 />} /> */}
                    <Route path={routes.story3} element={<Story3 />} />
                    <Route path={routes.subscriptionPlans} element={<SubscriptionPlans />} />
                    <Route path={routes.earnFreeSubscription} element={<EarnFreeSubscription />} />
                    <Route path={routes.blogLanding} element={<BlogLanding />} />
                    <Route path={routes.blog1} element={<Blog1 />} />
                    <Route path={routes.blog2} element={<Blog2 />} />
                    <Route path={routes.blog3} element={<Blog3 />} />
                    <Route path={routes.blog4} element={<Blog4 />} />
                    <Route path={routes.aprilEvent} element={<AprilEvent />} />
                    <Route path={routes.augustEvent} element={<AugustEvent />} />
                    {talentForumEnabled && <Route path={routes.octoberEvent} element={<OctoberEvent />} />}
                    <Route path={routes.claimProcess} element={<ClaimProcess />} />
                    <Route path={routes.verifyCompanyLead} element={<VerifyCompanyLead />} />
                    <Route path={routes.linkedin} element={<LinkedInCallback />} />
                    <Route path={routes.candidatePublicSearch} element={<CandidatePublicProfile />} />
                    <Route path={routes.companyPublicSearch} element={<CompanyPublicProfile />} />
                    <Route path={routes.candPrivatePage} element={<CandPrivatePage />} />
                    <Route path={routes.compPrivatePage} element={<CompPrivatePage />} />
                    <Route path={routes.candClaimRoute} element={<CandidateClaimRoute />} />
                    <Route path={routes.keka} element={<Keka />} />

                    {/* <ProtectedRoute path={routes.main} element={<Room />} /> */}
                    {/* <Route
                        path={routes.main}
                        element={
                            <ProtectedRoute>
                                <Room />
                            </ProtectedRoute>
                        }
                    /> */}
                </Routes>
            </Suspense>
            <ToastContainer {...toastContainerProps} />
            {/* </HistoryRouter> */}
        </>
    );
};

export default AppRoutes;
